import { Fragment, useState } from 'react';
import {
  StatusAborted,
  StatusError,
  StatusOK,
  StatusWarning,
} from '@backstage/core-components';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { packageListFor } from '@internal/plugin-scorecards-common';
import type { FactValue, RuleResult } from '@internal/plugin-scorecards-common';
import { PackageList } from './PackageList';

const STATUS_LABEL = {
  pass: <StatusOK>pass</StatusOK>,
  warn: <StatusWarning>warn</StatusWarning>,
  fail: <StatusError>fail</StatusError>,
  skipped: <StatusAborted>skipped</StatusAborted>,
} as const;

function formatValue(value: FactValue | null): string {
  return value === null ? 'no data' : String(value);
}

/** The DOM id of the panel holding one package list, safe for `aria-controls`. */
function panelId(prefix: string, ruleKey: string, factKey: string): string {
  return `packages-${prefix}-${ruleKey}-${factKey}`.replace(
    /[^A-Za-z0-9_-]/g,
    '_',
  );
}

/** One row per rule: status, the fact values it saw, weight, data freshness. */
export function RuleResultsTable({
  rules,
  entityRef,
  idPrefix = '',
  label = 'Rule results',
}: {
  rules: RuleResult[];
  /** Enables the package drill-down on package facts; without it none is offered. */
  entityRef?: string;
  /** Keeps the panel ids unique when a page shows several tables. */
  idPrefix?: string;
  /** The accessible name of the table. */
  label?: string;
}) {
  // `<rule key>|<fact key>` of every open package list.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setOpen(previous => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          <TableCell>Rule</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Values</TableCell>
          <TableCell align="right">Weight</TableCell>
          <TableCell>Data</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rules.length === 0 && (
          <TableRow>
            <TableCell colSpan={5}>No rules</TableCell>
          </TableRow>
        )}
        {rules.map(rule => {
          const values = Object.entries(rule.values ?? {});
          return (
            <Fragment key={rule.key}>
              <TableRow>
                <TableCell>{rule.title}</TableCell>
                <TableCell>
                  {STATUS_LABEL[rule.status] ?? rule.status}
                </TableCell>
                <TableCell>
                  {values.map(([key, value]) => {
                    const id = `${rule.key}|${key}`;
                    const isOpen = open.has(id);
                    return (
                      <div key={key}>
                        {key} = {formatValue(value)}
                        {entityRef && value !== null && packageListFor(key) && (
                          <Button
                            size="small"
                            color="primary"
                            aria-expanded={isOpen}
                            aria-controls={panelId(idPrefix, rule.key, key)}
                            aria-label={`Packages for ${key}`}
                            onClick={() => toggle(id)}
                          >
                            {isOpen ? 'Hide packages' : 'Show packages'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </TableCell>
                <TableCell align="right">{rule.weight}</TableCell>
                <TableCell>{rule.dataMissing ? 'missing' : 'ok'}</TableCell>
              </TableRow>
              {entityRef &&
                values
                  .filter(
                    ([key, value]) =>
                      value !== null && open.has(`${rule.key}|${key}`),
                  )
                  .map(([key, value]) => (
                    <TableRow key={`${rule.key}|${key}`}>
                      <TableCell
                        colSpan={5}
                        id={panelId(idPrefix, rule.key, key)}
                      >
                        <PackageList
                          entityRef={entityRef}
                          fact={key}
                          ruleValue={value}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
